import {
  createContext,
  Fragment,
  ReactElement,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';

import { UseMutationResult } from '@tanstack/react-query';
import styled from 'styled-components';

import { Collapse } from '@components/animators/collapse';
import { Avatar } from '@components/avatar';
import { Button } from '@components/button';
import { Card } from '@components/card';
import { UploadedFile } from '@components/file-field';
import { FormValidatorType, useFormField } from '@components/form';
import { FormError } from '@components/form-error';
import { FormDataType, FormInstance } from '@components/form/create-form';
import { useConfirmModal, useDeleteModal } from '@components/generic-modals';
import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconCheckmark } from '@components/icons/checkmark';
import { IconEdit } from '@components/icons/edit';
import { IconErrorSmall } from '@components/icons/error-small';
import { IconFile } from '@components/icons/file';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconInfo } from '@components/icons/info';
import { IconJourneyItem } from '@components/icons/journey-icon';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenPreview } from '@components/icons/open-preview';
import { IconPlay } from '@components/icons/play';
import { IconRedo } from '@components/icons/redo';
import { IconRestart } from '@components/icons/restart';
import { IconSkip } from '@components/icons/skip';
import { IconSubIndicator } from '@components/icons/sub-indicator';
import { IconTextFormatLinkOff } from '@components/icons/text-format-link-off';
import { IconTraveller } from '@components/icons/traveller';
import { IconTrigger } from '@components/icons/trigger';
import { IconUnassigned } from '@components/icons/unassigned';
import { IconUpload } from '@components/icons/upload';
import { InfoBox as CoreInfoBox, InfoBox, InfoBoxProps } from '@components/info-box';
import { LabelTextGroup } from '@components/label-text-group';
import { RelativeSpinner, Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import { PopoverSelect } from '@components/popover-select';
import { ProgressBar } from '@components/progress-bar';
import { Select } from '@components/select';
import { SelectOption } from '@components/selectmulti';
import { hideSidebar, Sidebar, SimpleHeaderProps } from '@components/sidebar';
import { Stack } from '@components/stack';
import { StateGroup, StateGroupProps } from '@components/state-selector/state-group';
import { StatusIndicatorProps } from '@components/status-indicator';
import { Text } from '@components/text';
import {
  FieldMappingGroup,
  FieldMappingVariables,
  VariableValue,
} from '@components/text-editors/addons/field-mapping';
import { EditorValue } from '@components/text-editors/text-editor';
import { showError } from '@components/toaster';
import { Tooltip } from '@components/tooltip';
import { getScopedTranslate, styledTranslate, useScopedTranslate } from '@locale/translate';
import { Spacing, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';
import { abbreviateDate } from '@utils/date';
import { hourOptions, minuteOptions } from '@utils/hoursOptions';
import { formatNumber } from '@utils/numbers';
import {
  fileNameFromPath,
  getBase64FileContentType,
  getBase64FileEnding,
  isBase64File,
} from '@utils/strings';

import {
  AttributeType,
  AttributeFieldType as RequestAttributeFieldType,
  TravellerAttributeTemplate,
} from 'api/attributes/requests';
import { useUserOption } from 'api/authentication/hooks';
import { CompanyUserOptions } from 'api/authentication/requests';
import { useUploadFile } from 'api/files/hooks';
import { FormFile } from 'api/journey-action-forms/requests';
import { useList } from 'api/lists/hooks';
import {
  useActivityLogs,
  useJourneyTravellerAction,
  useBypassTravellerAction,
  useResetFromAction,
  useRunJourneyTravellerAction,
  useSkipJourneyTravellerAction,
  useTravellerJourneyActions,
  useUnskipJourneyTravellerAction,
  useUpdateJourneyTravellerAction,
} from 'api/traveller-actions/hooks';
import {
  listBreakingReferencesBySkip,
  TravellerActionGroupAction,
} from 'api/traveller-actions/requests';
import {
  ACTION_TYPE,
  BASE_ACTION_TYPE,
  JOURNEY_ACTION_TYPE,
  JourneyField,
  TravellerJourneyAction,
  TravellerJourneyActionDetailed,
  TravellerJourneyActionRunStatus,
} from 'api/types';
import { useActiveWorkspaceUsers } from 'api/users/hooks';
import { User } from 'api/users/requests';
import {
  getActionRatio,
  getActionStatus,
  useCurrentWorkspace,
} from 'ui/authenticated/main/helpers';
import { Assignees, GenericActionCard } from 'ui/journey-builder/node-types/common';
import { showActionSidebar } from 'ui/traveller-journey-builder/action-sidebar';
import {
  actionErrorTitle,
  NodeExecutionMethod,
} from 'ui/traveller-journey-builder/node-types/common';

import { getActionIcon, getActionIconColor, getActionLabel } from './action-config';
import { ActionIcon } from './action-config';
import { ActivityTimeline } from './activity-timeline';
import { getFieldMappingId } from './helpers';

export const TEMPLATE_PREVIEW_BUTTON_CLASSNAME = 'template-preview-button';

export type SidebarProps = {
  shortId: TravellerJourneyActionDetailed['shortId'];
  isEdit: boolean;
};

export type BaseContentProps<T> = {
  action: T;
};

type TravellerActionHeaderProps = Omit<SimpleHeaderProps, 'title' | 'icon' | 'iconColor'> & {
  actionType: ACTION_TYPE;
  title?: string;
};

export const TravellerActionHeader: React.FC<TravellerActionHeaderProps> = ({
  actionType,
  title,
  ...rest
}) => (
  <Sidebar.SimpleHeader
    title={title ?? getActionLabel(actionType)}
    icon={getActionIcon(actionType)}
    iconColor={getActionIconColor(actionType)}
    {...rest}
  />
);

export type ActionTab = {
  label: string;
  content: JSX.Element;
  tabCount?: number;
};

export const useActionTabs = ({
  actionId,
  activityCount,
  display,
  edit,
  extraTabs,
}: {
  actionId: string;
  activityCount: number;
  display: JSX.Element;
  edit: JSX.Element;
  extraTabs?: ActionTab[];
}) => {
  const { isEditing } = useEditingContext();
  const { t } = useScopedTranslate(locale);
  return {
    tabs: [
      { label: t('details'), content: isEditing ? edit : display },
      {
        label: t('activity'),
        tabCount: activityCount > 0 ? activityCount : undefined,
        content: <ActivityTimeline actionId={actionId} />,
      },
      ...(extraTabs ?? []),
    ],
  };
};

type ContentComponent<T extends BASE_ACTION_TYPE> = React.ComponentType<{
  action: TravellerJourneyActionDetailed<T>;
}>;

export const TravellerActionSidebar = <T extends BASE_ACTION_TYPE>({
  shortId,
  actionType,
  Details,
  Edit,
}: {
  shortId?: string;
  actionType: T;
  Details: ContentComponent<T>;
  Edit: ContentComponent<T>;
}) => {
  const travellerActionQuery = useJourneyTravellerAction<T>(shortId);
  const { data: activityLogs } = useActivityLogs(shortId);
  const activityCount = activityLogs?.length ?? 0;
  const action = travellerActionQuery.data;

  const { tabs } = useActionTabs({
    actionId: shortId ?? '',
    activityCount,
    display: action ? <Details action={action} /> : <></>,
    edit: action ? <Edit action={action} /> : <></>,
  });

  if (travellerActionQuery.isLoading) {
    return <Spinner />;
  }
  if (!shortId || !action) {
    return <div>Data not found</div>;
  }

  return (
    <Sidebar>
      <TravellerActionHeader actionType={actionType} tabs={tabs}>
        <TravellerActionActions action={action} />
      </TravellerActionHeader>
    </Sidebar>
  );
};

export type TriggerMethod = 'automatic' | 'manual';

export const ActionErrorMessage = ({
  errorMessage,
}: {
  errorMessage: { title?: string; description?: string; date?: Date };
}) => {
  return (
    <div>
      {errorMessage.title && <FormError text={errorMessage.title} isBold />}
      {errorMessage.description && (
        <Text font="regular14" color="elementRed100" text={errorMessage.description} />
      )}
      {errorMessage.date && (
        <Text
          font="regular14"
          color="elementRed100"
          text={abbreviateDate(errorMessage.date, 'en', 'datetime')}
        />
      )}
    </div>
  );
};

export const ActionProgressBar = ({
  action,
  progressText,
  isError,
  isCard = true,
  errorMessage,
  dateSuccess,
  progressPercentage,
  ratioOverride,
}: {
  action: TravellerJourneyAction;
  progressText?: string;
  isError?: boolean;
  isCard?: boolean;
  errorMessage?: { title?: string; description?: string; date?: Date };
  dateSuccess?: Date;
  progressPercentage?: string;
  ratioOverride?: number;
}) => {
  const { status } = action;
  const { t } = useScopedTranslate(locale);
  const ratio = ratioOverride ?? getActionRatio(action);

  const computedErrorMessage =
    errorMessage ??
    (isError
      ? {
          title: actionErrorTitle[action.action],
          description: action.result?.message,
          date: action.result?.dateFailed,
        }
      : undefined);

  const computedDateSuccess = dateSuccess ?? action.result?.dateSucceeded;

  let percentage = null;
  if (computedDateSuccess) {
    percentage = abbreviateDate(computedDateSuccess, 'en', 'datetime');
  } else if (!computedErrorMessage) {
    percentage = progressPercentage ?? `${ratio * 100}%`;
  }

  const content = (
    <Stack space={4}>
      <Stack space={1}>
        <Stack orientation="horizontal" justify="space-between">
          <Text
            font="regular14"
            color="textSecondary"
            text={
              progressText ??
              (status === 'completed'
                ? t('completed')
                : isError
                ? t('error')
                : status === 'in_progress'
                ? t('inProgress')
                : t('notStarted'))
            }
          />
          {percentage ? <Text font="regular14" color="textSecondary" text={percentage} /> : null}
        </Stack>
        <ProgressBar ratio={ratio} variant={isError ? 'error' : undefined} />
      </Stack>
      {computedErrorMessage && <ActionErrorMessage errorMessage={computedErrorMessage} />}
    </Stack>
  );

  return isCard ? <Card space={4}>{content}</Card> : content;
};

export const isActionError = (
  action?: TravellerJourneyActionDetailed | TravellerActionGroupAction
) => {
  if (!action) {
    return false;
  }
  return action.status !== 'completed' && !!action.result && !!action.result.dateFailed;
};

export const isActionNotStarted = (action: TravellerJourneyActionDetailed) => {
  return action.status !== 'completed' && !action.result;
};

const TravellerActionUpdateDisclamer = () => {
  const { t } = useScopedTranslate(locale);
  return <Text font="regular14" text={t('travellerActionDisclamer')} color="textHelper" />;
};

type ActionProps = Partial<StatusIndicatorProps> & { action: TravellerJourneyActionDetailed };

const AssigneesSelect = ({ action, ...indicatorProps }: ActionProps) => {
  const { shortId: workspaceId } = useCurrentWorkspace();
  const usersQuery = useActiveWorkspaceUsers(workspaceId);
  const updateQuery = useUpdateJourneyTravellerAction(action);
  const { t } = useScopedTranslate(locale);

  const users = (usersQuery.data ?? []).map((user) => ({
    label: user.name,
    icon: <Avatar name={user.name} src={user.picture} />,
    value: user.id,
  }));

  return (
    <PopoverSelect
      isMulti
      trigger={<Assignees assignees={action.assignees} {...indicatorProps} />}
      items={users}
      clear={{ label: t('noAssignee'), icon: <IconUnassigned color="purple20" size={28} /> }}
      searchPlaceholder={t('assignTo')}
      onChange={(assignees) => updateQuery.mutate({ assignees })}
      defaultValue={action.assignees?.map((assignee) => assignee.id)}
      placement="bottom-end"
    />
  );
};

export const NodeMenu = ({
  actionId,
  actionType,
  hasFlowReached,
  isAutomatic,
  isSkipped,
  isCompleted,
  isHalted,
  travellerJourneyId,
}: {
  actionId: string;
  actionType: JOURNEY_ACTION_TYPE;
  hasFlowReached?: boolean;
  isAutomatic?: boolean;
  isSkipped?: boolean;
  isCompleted?: boolean;
  isHalted?: boolean;
  travellerJourneyId?: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const travellerActionsQuery = useTravellerJourneyActions(travellerJourneyId ?? '');
  const actions = travellerActionsQuery.data ?? [];
  const skipMutation = useSkipJourneyTravellerAction(actionId);
  const unskipMutation = useUnskipJourneyTravellerAction(actionId);
  const resetFromMutation = useResetFromAction(actionId);
  const bypassMutation = useBypassTravellerAction(actionId);
  const showConfirmModal = useConfirmModal('LARGE');
  const showRestartModal = useDeleteModal();

  const showSkipItems = !isCompleted && actionType !== 'break_loop';

  const actionNode = actions.find(
    (action) => action.shortId === actionId
  ) as TravellerJourneyAction;

  const handleSkipAction = async () => {
    let dependantIds: string[] = [];
    try {
      dependantIds = await listBreakingReferencesBySkip(actionId);
    } catch (e) {
      showError(t('core.errors.generic'));
    }

    const confirmationNodes = dependantIds
      .map((dependantId) => actions.find((action) => action.shortId === dependantId))
      .filter((node): node is TravellerJourneyAction => node !== undefined);

    const allBreakingNodesAreSkipped = confirmationNodes.every(
      (breakingNode) => breakingNode.isSkipped
    );

    if (!allBreakingNodesAreSkipped && confirmationNodes.length > 0) {
      showConfirmModal({
        title: t('warning'),
        headerIcon: <IconFormIndicatorWarning />,
        description: t('skipWarningText'),
        buttonText: t('skipAction'),
        buttonIcon: <IconSkip />,
        buttonVariant: 'primary-blue',
        onConfirm: () => skipMutation.mutateAsync(),
        children: (
          <Stack space={2}>
            <Text font="semibold14">{t('confirmActionLabel')}</Text>
            {confirmationNodes.map((breakingNode) => (
              <Stack
                key={`${breakingNode.shortId}-${actionId}`}
                orientation="horizontal"
                justify="space-between"
                align="center"
                space={1}
              >
                <GenericActionCard action={actionNode} />
                <IconTextFormatLinkOff color="elementRed100" />
                <GenericActionCard action={breakingNode} />
              </Stack>
            ))}
          </Stack>
        ),
      });
    } else {
      skipMutation.mutateAsync();
    }
  };

  const handleRestartFromHere = () => {
    const calculateDescendants = (actionId: string): number => {
      const childActions = actions.filter((a) => a.parentAction === actionId);
      let count = 0;

      for (const child of childActions) {
        const isCompleted = child.status === 'completed';
        const hasSucceeded = Boolean(child.result?.dateSucceeded);
        const hasStarted = !hasSucceeded && Boolean(child.result?.dateStarted);

        if (isCompleted || hasSucceeded || hasStarted) {
          count += 1;
        }

        count += calculateDescendants(child.shortId);
      }

      return count;
    };

    const numDescendants = calculateDescendants(actionId);
    const numActionsToReset = numDescendants + 1;
    showRestartModal({
      hideCannotBeUndone: true,
      hasUserConfirmation: true,
      title: t('restartModalTitle'),
      headerIcon: <IconFormIndicatorWarning />,
      description: t('restartDescription'),
      buttonText: t('restart'),
      buttonIcon: <IconRestart />,
      buttonVariant: 'primary-blue',
      onConfirm: () => resetFromMutation.mutateAsync(),
      children: (
        <Stack space={4}>
          <Card
            space={4}
            style={{ backgroundColor: theme.color.blueTrans5, paddingRight: theme.space[6] }}
          >
            <Stack orientation="horizontal" justify="space-between">
              <Stack orientation="horizontal" space={2} align="center">
                <IconTrigger color="elementPurple60" />
                <Text font="bold14" text={t('actionsTobeReset')} />
              </Stack>
              <Text font="bold14" color="textSecondary" text={numActionsToReset} />
            </Stack>
          </Card>
          <Text color="red100" text={t('cannotBeUndone')} />
        </Stack>
      ),
    });
  };

  const isConditionAction = actionType === 'condition' || actionType === 'condition_v2';
  const showBypassButton = isConditionAction && isHalted && hasFlowReached;

  const handleBypass = () => {
    showConfirmModal({
      title: t('overrideConditionTitle'),
      headerIcon: <IconFormIndicatorWarning />,
      description: t('overrideConditionDescription'),
      buttonText: t('overrideCondition'),
      buttonVariant: 'primary-blue',
      onConfirm: () => bypassMutation.mutateAsync(),
    });
  };

  const disallowedActionTypesForRestart = [
    'condition_v2',
    'condition',
    'wait_for_time',
    'wait_until_fixed_date',
    'wait_until_relative_date',
    'wait_for_attribute_change',
    'break_loop',
    'formatter',
  ];
  const showRestartFromHereButton = !disallowedActionTypesForRestart.includes(actionType);

  return (
    <Menu buttonProps={{ variant: 'ghost', iconRight: <IconMoreVertical /> }}>
      <Menu.Item
        label={t('editAction')}
        isDisabled={isCompleted}
        icon={<IconEdit />}
        onSelect={() => showActionSidebar({ id: actionId, type: actionType }, true)}
      />
      {showSkipItems && (
        <>
          {isSkipped ? (
            <Menu.Item
              label={t('unskipAction')}
              icon={<IconPlay />}
              onSelect={() =>
                isAutomatic && hasFlowReached
                  ? showConfirmModal({
                      title: t('confirmUnskip'),
                      description: t('confirmText'),
                      buttonText: t('core.confirm'),
                      buttonVariant: 'primary',
                      onConfirm: () => unskipMutation.mutateAsync(),
                    })
                  : unskipMutation.mutateAsync()
              }
            />
          ) : (
            <Menu.Item
              label={t('skipAction')}
              icon={<IconSkip />}
              onSelect={() => handleSkipAction()}
            />
          )}
        </>
      )}
      {showRestartFromHereButton && (
        <Menu.Item
          label={t('restartFromHere')}
          icon={<IconRestart />}
          onSelect={() => handleRestartFromHere()}
          isDisabled={!isCompleted}
        />
      )}
      {showBypassButton && (
        <Menu.Item
          label={t('overrideCondition')}
          tooltip={t('overrideConditionTooltip')}
          icon={<IconPlay />}
          onSelect={() => handleBypass()}
        />
      )}
    </Menu>
  );
};

export const TravellerActionActions = ({
  action,
  ratioOverride,
  statusOverride,
}: {
  action: ActionProps['action'];
  ratioOverride?: number;
  statusOverride?: TravellerJourneyActionRunStatus;
}) => {
  const { isEditing } = useEditingContext();
  const isError = isActionError(action);
  const ratio = ratioOverride ?? getActionRatio(action);
  const status = statusOverride ?? getActionStatus(action);
  const variant =
    status === 'completed' ? 'completed' : status === 'in_progress' ? 'inProgress' : 'notStarted';

  const indicatorProps = {
    variant,
    ratio,
  } as StatusIndicatorProps;

  if (isEditing || action.action === 'journey_start') {
    return null;
  }

  return (
    <>
      {action.isAutomatic || status === 'skipped' || status === 'halted' ? (
        <NodeExecutionMethod
          action={action}
          overwriteRatio={ratio}
          overwriteVariant={variant}
          overwriteError={isError}
        />
      ) : (
        <AssigneesSelect action={action} {...indicatorProps} />
      )}
      <NodeMenu
        actionId={action.shortId}
        actionType={action.action}
        hasFlowReached={action.hasFlowReached}
        isSkipped={action.isSkipped}
        isCompleted={action.status === 'completed'}
        isHalted={action.isHalted}
        isAutomatic={action.isAutomatic}
        travellerJourneyId={action.travellerJourneyId}
      />
    </>
  );
};

export const ActionResultCard = ({
  action,
  title,
  icon,
  progressText,
  errorTitle,
  children,
  onPreview,
  hideStatus,
}: {
  action: TravellerJourneyActionDetailed;
  title: string;
  icon: ReactElement<IconProps>;
  progressText?: string;
  errorTitle?: string;
  children?: ReactNode;
  onPreview?: () => void;
  hideStatus?: boolean;
}) => {
  const isError = isActionError(action);

  return (
    <Card space={4}>
      <Stack space={4}>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack orientation="horizontal" space={2} align="center">
            {icon}
            <Text font="semibold14" text={title} />
          </Stack>
          <Stack orientation="horizontal" space={2} align="center">
            <Stack orientation="horizontal" space={2} align="center">
              <div>{isError && <IconErrorSmall color="error" />}</div>
              {action.isConfigured && onPreview && (
                <IconButton
                  icon={<IconOpenPreview />}
                  dataTestid="preview"
                  variant="ghost-purple"
                  onClick={() => onPreview()}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
        {!hideStatus && (
          <ActionProgressBar
            action={action}
            progressText={progressText}
            isError={isError}
            errorMessage={
              isError && errorTitle
                ? {
                    title: errorTitle,
                    description: action.result?.message,
                    date: action.result?.dateFailed,
                  }
                : undefined
            }
            isCard={false}
          />
        )}
        {children}
      </Stack>
    </Card>
  );
};

export const JourneyActionCard = ({
  title,
  description,
  icon,
  children,
  spaceX = 4,
  spaceY = 2,
}: {
  title: string;
  description?: string;
  icon: ReactElement<IconProps>;
  children?: ReactNode;
  spaceX?: Spacing;
  spaceY?: Spacing;
}) => {
  return (
    <Card spaceX={spaceX} spaceY={spaceY}>
      <Stack space={4}>
        <Stack orientation="horizontal" align="center" justify="space-between">
          <Stack orientation="horizontal" space={2} align="center">
            {icon}
            <div>
              <Text font="semibold14" text={title} />
              {description && <Text font="regular14" color="textSecondary" text={description} />}
            </div>
          </Stack>
        </Stack>
        {children}
      </Stack>
    </Card>
  );
};

const EditingContext = createContext({
  isEditing: false,
  startEditing: () => {},
  stopEditing: () => {},
});

export const useEditingContext = () => useContext(EditingContext);

export const EditingProvider: React.FC<{ defaultIsEditing?: boolean }> = ({
  defaultIsEditing = false,
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(defaultIsEditing ?? false);
  const startEditing = () => setIsEditing(true);
  const stopEditing = () => setIsEditing(false);

  const value = {
    isEditing,
    startEditing,
    stopEditing,
  };

  return <EditingContext.Provider value={value} {...props} />;
};

export const TravellerActionFooter = ({
  action,
  startIcon,
  startText,
  retryText,
}: {
  action: TravellerJourneyActionDetailed;
  startIcon: ReactElement<IconProps>;
  startText: string;
  retryText: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const runMutation = useRunJourneyTravellerAction(action, {
    onSuccess: hideSidebar,
  });
  const { startEditing } = useEditingContext();

  const isNotStarted = isActionNotStarted(action);
  const isError = isActionError(action);
  const isSmsAction = action.action === 'send_sms';
  const isActionCompleted = action.status === 'completed';

  if (isSmsAction && !isActionCompleted && !isNotStarted) {
    return <SmsInProgressFooter action={action} retryText={retryText} runMutation={runMutation} />;
  }

  if (!isNotStarted && !isError) {
    return null;
  }

  return (
    <Sidebar.Footer>
      <Stack orientation="horizontal" justify="space-between">
        <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
        {isNotStarted && action.hasJourneyStarted && !action.isJourneyPaused && (
          <Button
            variant="primary-blue"
            iconLeft={startIcon}
            text={startText}
            onClick={() => runMutation.mutate({})}
            tooltip={!action.isConfigured ? t('actionNotConfigured') : ''}
            isDisabled={runMutation.isLoading || !action.isConfigured || action.isSkipped}
          />
        )}
        {isError && (
          <Button
            variant="secondary"
            iconLeft={<IconRedo />}
            text={retryText}
            onClick={() => runMutation.mutate({})}
            tooltip={!action.isConfigured ? t('actionNotConfigured') : ''}
            isDisabled={runMutation.isLoading || !action.isConfigured}
          />
        )}
      </Stack>
    </Sidebar.Footer>
  );
};

const SmsInProgressFooter = ({
  action,
  retryText,
  runMutation,
}: {
  action: TravellerJourneyActionDetailed;
  retryText: string;
  runMutation: UseMutationResult;
}) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Sidebar.Footer>
      <Stack orientation="horizontal" justify="flex-end">
        <Button
          variant="secondary"
          iconLeft={<IconRedo />}
          text={retryText}
          onClick={() => runMutation.mutate({})}
          tooltip={!action.isConfigured ? t('actionNotConfigured') : ''}
          isDisabled={runMutation.isLoading || !action.isConfigured}
        />
      </Stack>
    </Sidebar.Footer>
  );
};

export const ActionTimeSelector = ({
  defaultHour,
  defaultMinute,
  customHourOptions,
  customMinuteOptions,
  isRequired = true,
  isDisabled = false,
}: {
  defaultHour?: string;
  defaultMinute?: string;
  customHourOptions?: { label: string; value: string }[];
  customMinuteOptions?: { label: string; value: string }[];
  isRequired?: boolean;
  isDisabled?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);

  const hourFormProps = useFormField({
    id: 'hour',
    label: t('hour'),
    validators: isRequired ? ['required'] : [],
    defaultValue: defaultHour,
  });
  const minuteFormProps = useFormField({
    id: 'minute',
    label: t('minute'),
    validators: isRequired ? ['required'] : [],
    defaultValue: defaultMinute,
  });

  const selectedHourOptions = customHourOptions ?? hourOptions;
  const selectedMinuteOptions = customMinuteOptions ?? minuteOptions;

  return (
    <TimeContainer>
      <Select
        {...hourFormProps}
        isClearable={false}
        options={selectedHourOptions}
        isDisabled={isDisabled}
      />
      <Select
        {...minuteFormProps}
        isClearable={false}
        options={selectedMinuteOptions}
        isDisabled={isDisabled}
      />
    </TimeContainer>
  );
};

const TimeContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 4,
})({
  '>div': {
    width: '100%',
  },
});

export const getWaitForTimeLength = (days?: string, hours?: string, minutes?: string) => {
  const { t } = getScopedTranslate(locale);
  const timeParts = [];
  if (days && days !== '0') {
    timeParts.push(days === '1' ? t('nrDay') : t('nrDays', { nr: days }));
  }
  if (hours && hours !== '0') {
    timeParts.push(hours === '1' ? t('nrHour') : t('nrHours', { nr: hours }));
  }
  if (minutes && minutes !== '0') {
    timeParts.push(minutes === '1' ? t('nrMinute') : t('nrMinutes', { nr: minutes }));
  }
  return timeParts.join(', ');
};

type WaitForDateStateGroupProps = Pick<StateGroupProps, 'onStateSelected' | 'selectedStateId'>;

export const WaitForDateStateGroup = (props: WaitForDateStateGroupProps) => {
  const { t } = useScopedTranslate(locale);

  return (
    <StateGroup
      orientation="horizontal"
      isDisabled={!Boolean(props.onStateSelected)}
      stateItems={[
        {
          id: 'wait_until_relative_date',
          orientation: 'vertical',
          icon: <IconJourneyItem />,
          label: t('journeyDate'),
          description: t('dateValue'),
        },
        {
          id: 'wait_until_fixed_date',
          orientation: 'vertical',
          icon: <IconCheckmark />,
          label: t('selectDate'),
          description: t('fixedDate'),
        },
      ]}
      {...props}
    />
  );
};

type FormSidebarFooterProps = {
  Form: any;
  editMutation: UseMutationResult;
  isDirty: boolean;
  hasNotifyError?: boolean;
};

export const FormSidebarFooter = ({
  Form,
  isDirty,
  editMutation,
  hasNotifyError,
}: FormSidebarFooterProps) => {
  const { t } = useScopedTranslate(locale);
  const { stopEditing } = useEditingContext();

  return (
    <>
      <Sidebar.Separator />
      <TravellerActionUpdateDisclamer />

      <Sidebar.Footer>
        <Form.Actions space={2}>
          <Button variant="secondary" text={t('core.cancel')} onClick={stopEditing} />
          <Form.Submit
            variant="primary-blue"
            text={t('core.save')}
            isDisabled={!isDirty || editMutation.isLoading || hasNotifyError}
          />
        </Form.Actions>
      </Sidebar.Footer>
    </>
  );
};

type AttachmentInfo = {
  label: string;
  isAttribute: boolean;
  isUploaded: boolean;
  isActionMapped: boolean;
  referenceActionId?: string;
};

const MAPPED_SOURCE_ICON_SIZE = 16;
export const MappedSource = ({
  label,
  isAttribute,
  isActionMapped,
  isUploaded,
  referenceActionId,
}: AttachmentInfo) => {
  const { t } = useScopedTranslate(locale);
  const referenceActionQuery = useJourneyTravellerAction(referenceActionId);
  const referenceAction = referenceActionQuery.data;
  return (
    <Card space={3}>
      <Stack space={1} orientation="horizontal" align="center">
        <MappedSourceIconContainer>
          {isActionMapped && referenceAction ? (
            <ActionIcon action={referenceAction.action} size={MAPPED_SOURCE_ICON_SIZE} />
          ) : isAttribute ? (
            <IconTraveller size={MAPPED_SOURCE_ICON_SIZE} />
          ) : (
            <IconUpload size={MAPPED_SOURCE_ICON_SIZE} />
          )}
        </MappedSourceIconContainer>
        <Text
          font="regular12"
          text={
            isActionMapped && referenceAction
              ? getActionLabel(referenceAction.action)
              : isAttribute
              ? t('traveller')
              : t('manuallyUploded')
          }
        />
      </Stack>
      {!isUploaded && (
        <Stack space={1} orientation="horizontal" align="center">
          <IconSubIndicator size={MAPPED_SOURCE_ICON_SIZE} color="elementPurple50" />
          <IconFile size={MAPPED_SOURCE_ICON_SIZE} color="elementPurple50" />
          <Text font="regular12" text={label} />
        </Stack>
      )}
    </Card>
  );
};

const MappedSourceIconContainer = styled.div({
  minWidth: pxToRem(MAPPED_SOURCE_ICON_SIZE),
  minHeight: pxToRem(MAPPED_SOURCE_ICON_SIZE),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const AttachmentList = ({
  attachments,
  showAsCards = false,
}: {
  attachments: string[];
  showAsCards?: boolean;
}) => {
  const FileAsCard = ({ attachment }: { attachment: string }) => {
    return (
      <UploadedFile
        iconColor="elementPurpleInvert"
        font="regular14"
        name={fileNameFromPath(attachment) || attachment}
      />
    );
  };

  return (
    <Stack space={showAsCards ? 2 : 0}>
      {attachments.map((attachment, idx) => (
        <Fragment key={idx}>
          {isBase64File(attachment) ? (
            <Base64DownlodableLink base64string={attachment} showAsCard={showAsCards} />
          ) : showAsCards ? (
            <FileAsCard attachment={attachment} />
          ) : (
            <Text font="regular14" text={fileNameFromPath(attachment) || attachment} />
          )}
        </Fragment>
      ))}
    </Stack>
  );
};

export const Base64DownlodableLink = ({
  base64string,
  showAsCard,
}: {
  base64string: string;
  showAsCard?: boolean;
}) => {
  const { t } = useScopedTranslate(locale);
  const contentType = getBase64FileContentType(base64string);
  const fileEnding = getBase64FileEnding(base64string);
  const filename = `${fileEnding}-file.${fileEnding}`;
  const b64string = base64string;
  const name = `${fileEnding?.toUpperCase()} ${t('file')}`;

  if (showAsCard) {
    return (
      <UploadedFile
        iconColor="elementPurpleInvert"
        font="regular14"
        name={name}
        base64File={{ filename: filename, href: `data:${contentType};base64,${b64string}` }}
      />
    );
  }

  return (
    <a download={filename} href={`data:${contentType};base64,${b64string}`}>
      <Text font="regular14" text={name} />
    </a>
  );
};

export type InfoBoxVariant = 'green' | 'blue' | 'indigo' | 'orange';

type InfoBoxClosableProps = InfoBoxProps & {
  optionKey: keyof CompanyUserOptions;
};

export const InfoBoxClosable = ({ optionKey, variant, text, icon }: InfoBoxClosableProps) => {
  const { value, update } = useUserOption(optionKey, '');
  if (value) {
    return null;
  }
  return (
    <InfoBox
      variant={variant}
      icon={icon}
      text={text}
      closable
      onClose={() => update.mutate(true)}
    />
  );
};

export const getFieldMappedAttributeFieldComparatorIds = (id: string) => {
  return {
    valueId: `${id}Value`,
  };
};

type FieldMappedAttributeFieldCommonFieldProps = {
  id: any;
  label: string;
  validators?: FormValidatorType[];
  defaultValue?: VariableValue;
  fieldMappingId: string;
};

type FieldMappedAttributeFieldProp<T> = {
  field: JourneyField;
  attribute?: AttributeFieldType;
  usersList?: User[];
  form: FormInstance<T>;
  variableGroups: FieldMappingGroup[];
  emailVariableGroups: FieldMappingGroup[];
  phoneVariableGroups: FieldMappingGroup[];
  dateVariableGroups: FieldMappingGroup[];
  optionsVariableGroups: FieldMappingGroup[];
  fileVariableGroups: FieldMappingGroup[];
  fileMultiVariableGroups: FieldMappingGroup[];
  numberVariableGroups: FieldMappingGroup[];
  decimalVariableGroups: FieldMappingGroup[];
  filePath: string;
  overwriteLabel?: string;
  overwriteValueRequired?: boolean;
  isDisabled?: boolean;
  savedAttributeType?: RequestAttributeFieldType;
  savedAttributeOptions?: string;
  journeyId: string;
  overwriteValueId?: string;
};

export const FieldMappedAttributeField = <T extends FormDataType>({
  field,
  attribute,
  form: Form,
  usersList,
  variableGroups,
  emailVariableGroups,
  phoneVariableGroups,
  dateVariableGroups,
  optionsVariableGroups,
  fileVariableGroups,
  fileMultiVariableGroups,
  numberVariableGroups,
  decimalVariableGroups,
  filePath,
  overwriteLabel,
  overwriteValueRequired = false,
  isDisabled = false,
  savedAttributeType,
  savedAttributeOptions,
  journeyId,
  overwriteValueId,
}: FieldMappedAttributeFieldProp<T>) => {
  const { valueId } = getFieldMappedAttributeFieldComparatorIds(field.id);
  const fieldMappingId = getFieldMappingId(journeyId);
  const commonFieldProps = {
    id: overwriteValueId ?? valueId,
    label: overwriteLabel ?? attribute?.title,
    validators: overwriteValueRequired || attribute?.isRequired ? ['required'] : [],
    defaultValue: field.value as VariableValue,
    fieldMappingId,
  } as FieldMappedAttributeFieldCommonFieldProps;

  const users = usersList?.map((user) => ({ value: user.shortId, label: user.name })) ?? [];
  const listQuery = useList(attribute?.options ?? savedAttributeOptions ?? '');

  const uploadMutation = useUploadFile(filePath, false);

  const fieldType = attribute?.attributeType ?? savedAttributeType;

  switch (fieldType) {
    case 'text':
      return (
        <Form.FieldMappingTextEditor
          {...commonFieldProps}
          plaintext
          singleLine
          variableGroups={variableGroups}
          disabled={isDisabled}
        />
      );
    case 'paragraph':
      return (
        <Form.FieldMappingTextEditor
          {...commonFieldProps}
          plaintext
          variableGroups={variableGroups}
          disabled={isDisabled}
        />
      );
    case 'number':
      return (
        <Form.FieldMappingNumberField {...commonFieldProps} variableGroups={numberVariableGroups} />
      );
    case 'decimal':
      return (
        <Form.FieldMappingNumberField
          {...commonFieldProps}
          variableGroups={decimalVariableGroups}
          isDecimal
        />
      );
    case 'date':
      return (
        <Form.FieldMappingDatePicker
          {...commonFieldProps}
          variableGroups={dateVariableGroups}
          placeholder="DD / MM / YYYY"
          disabled={isDisabled}
        />
      );
    case 'user':
      return (
        <Form.Select
          {...commonFieldProps}
          options={users}
          defaultValue={field.value as string}
          isDisabled={isDisabled}
        />
      );
    case 'phone_number':
      return (
        <Form.FieldMappingPhoneField
          {...commonFieldProps}
          variableGroups={phoneVariableGroups}
          disabled={isDisabled}
        />
      );
    case 'email':
      return (
        <Form.EmailField
          {...commonFieldProps}
          variableGroups={emailVariableGroups}
          disabled={isDisabled}
        />
      );
    case 'single_select':
      if (listQuery.isLoading && listQuery.fetchStatus === 'fetching') {
        return <RelativeSpinner />;
      }
      return (
        <Form.FieldMappingSelectField
          {...commonFieldProps}
          defaultValue={field.value as VariableValue}
          variableGroups={optionsVariableGroups}
          options={(listQuery.data?.options ?? []).map((option) => ({
            value: option.shortId,
            label: option.label,
          }))}
          disabled={isDisabled}
        />
      );
    case 'multi_select':
      const values = ((field.value ?? []) as SelectOption[]).map((value) => value.value);
      const options = (listQuery.data?.options ?? []).map((option) => ({
        value: option.shortId,
        label: option.label,
      }));
      const defaultValue = options.filter((option) => values.includes(option.value));
      return (
        <Form.SelectMulti
          {...commonFieldProps}
          key={`multi-select-${options.length}`}
          defaultValue={defaultValue}
          options={options}
          isDisabled={isDisabled}
        />
      );
    case 'file':
      return (
        <Form.FieldMappingFileField
          {...commonFieldProps}
          variableGroups={fileVariableGroups}
          onSelect={uploadMutation.mutateAsync}
          disabled={isDisabled}
        />
      );
    case 'file_multi':
      return (
        <Form.FieldMappingFileListField
          {...commonFieldProps}
          defaultValue={field.value as VariableValue[]}
          variableGroups={fileMultiVariableGroups}
          onSelect={uploadMutation.mutateAsync}
          disabled={isDisabled}
        />
      );
    default:
      return null;
  }
};

export const formatDecimal = (value: unknown): string => {
  const str = String(value);
  const fractionDigits = str.includes('.') ? str.split('.')[1].length : 0;
  const num = Number(value);
  if (!Number.isFinite(num)) return str;
  const locale =
    typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';
  const effectiveLocale = locale.toLowerCase().startsWith('is') ? 'de-DE' : locale;
  return new Intl.NumberFormat(effectiveLocale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(num);
};

type FileValueType = string | FormFile | Array<string> | Array<FileValueType>;

const getFileNames = (value: FileValueType, accumulated: Array<string>) => {
  if (Array.isArray(value)) {
    value.forEach((item) => {
      getFileNames(item, accumulated);
    });
  } else if (typeof value === 'string') {
    try {
      const jsonObject = JSON.parse(value.replaceAll("'", '"'));
      if (Array.isArray(jsonObject)) {
        jsonObject.forEach((item) => {
          getFileNames(item, accumulated);
        });
      } else {
        accumulated.push(fileNameFromPath(jsonObject.src));
      }
    } catch (e) {
      accumulated.push(fileNameFromPath(value));
    }
  } else if (typeof value === 'object') {
    accumulated.push(fileNameFromPath(value.src));
  }
};

export type AttributeFieldType = AttributeType | TravellerAttributeTemplate;

export const RenderAttributeFieldLabelGroup = ({
  attribute,
  field,
  usersList,
  savedFieldName,
  savedFieldType,
  savedOptionValue,
  actionStatus,
}: {
  attribute?: AttributeFieldType;
  field: JourneyField;
  usersList?: User[];
  savedFieldName?: string;
  savedFieldType?: RequestAttributeFieldType;
  savedOptionValue?: string;
  actionStatus: TravellerJourneyActionRunStatus;
}) => {
  const { t } = useScopedTranslate(locale);
  const isActive = attribute?.isActive ?? false;
  const fieldName =
    attribute?.title ?? savedFieldName ?? field.last_saved_title ?? t('attributeNotFound');
  const showErrorMessage = actionStatus !== 'completed' && !isActive;

  const listQuery = useList(attribute?.options ?? field.last_saved_options ?? '');
  const listOptions = listQuery.data?.options ?? [];
  let value = field.value;

  const label = showErrorMessage ? (
    <Stack space={1} orientation="horizontal" align="center">
      <Tooltip content={t('fieldNotFound')}>
        <Stack orientation="horizontal" space={1}>
          <Text font="semibold14" color="purple50" text={fieldName} />
          <IconInfo color="purple50" size={16} />
        </Stack>
      </Tooltip>
    </Stack>
  ) : (
    fieldName
  );

  if (actionStatus === 'completed' && !savedFieldName) {
    return null;
  }
  const type = attribute?.attributeType ?? savedFieldType;
  if (value && type) {
    switch (type) {
      case 'date':
        value = abbreviateDate(value as string, 'en');
        break;
      case 'number':
        value = formatNumber(value as unknown as number);
        break;
      case 'decimal':
        value = formatDecimal(value);
        break;
      case 'boolean':
        value = String(value ?? false);
        break;
      case 'user':
        value = (usersList ?? []).find((user) => user.shortId === value)?.name ?? t('userNotFound');
        break;
      case 'single_select':
        value =
          listOptions.find((option) => option.shortId === value || option.label === value)?.label ??
          savedOptionValue ??
          t('optionNotFound');
        break;
      case 'multi_select':
        value = ((value ?? []) as SelectOption[])
          .map(
            (item) =>
              listOptions.find((option) => option.shortId === item.value)?.label ??
              ((savedOptionValue ?? []) as SelectOption[]).find(
                (option) => option.value === item.value
              )?.label ??
              undefined
          )
          .filter((value) => value !== undefined)
          .join('\n');
        break;
      case 'file':
      case 'file_multi':
        const files: Array<string> = [];
        getFileNames(value as FileValueType, files);
        value = files.join('\n');
        break;
    }
  }

  return <LabelTextGroup label={label} text={typeof value === 'string' ? value : ''} />;
};

const GSM_7_MAX_LENGTH = 160;
const UCS_2_MAX_LENGTH = 70;

export const SMSMessageCountWarning = ({
  message,
}: {
  message?: EditorValue<{
    variables?: FieldMappingVariables;
  }>;
}) => {
  const { t } = useScopedTranslate(locale);

  const messageLength = message?.value?.length ?? 0;

  const hasMessageVariables = (message?.variables ? Object.keys(message?.variables).length : 0) > 0;

  const gsm7Chars =
    '@£$¥èéùìòÇØøÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà\n';
  const containsNonGSM7Characters = (message?.value ?? '')
    .split('')
    .some((char) => !gsm7Chars.includes(char));

  const showInfoBox =
    messageLength > GSM_7_MAX_LENGTH ||
    hasMessageVariables ||
    (containsNonGSM7Characters && messageLength > UCS_2_MAX_LENGTH);

  return (
    <>
      {hasMessageVariables ? (
        <Text text={t('messageDynamicVaribles')} font="regular14" />
      ) : (
        <Text
          text={styledTranslate({
            key: 'messageLength',
            variables: {
              count: {
                text: messageLength,
                font: 'bold14',
              },
            },
            translations: locale,
          })}
          font="regular14"
        />
      )}
      {showInfoBox && (
        <CoreInfoBox
          text={t('warningText', {
            gsm7maxlength: GSM_7_MAX_LENGTH,
            ucs2maxlength: UCS_2_MAX_LENGTH,
          })}
          variant="orange"
          icon={<IconFormIndicatorWarning />}
        />
      )}
    </>
  );
};

export const DelayedCollapse = ({
  isCollapsed,
  delay,
  children,
}: {
  isCollapsed: boolean;
  delay: number;
  children: ReactNode;
}) => {
  const [shouldShow, setShouldShow] = useState(!isCollapsed);

  useEffect(() => {
    if (!isCollapsed) {
      const timer = setTimeout(() => {
        setShouldShow(true);
      }, delay);
      return () => clearTimeout(timer);
    } else {
      setShouldShow(false);
    }
  }, [isCollapsed, delay]);

  return <Collapse isCollapsed={!shouldShow}>{children}</Collapse>;
};

const locale = {
  en: {
    inProgress: 'In progress',
    notStarted: 'Not started',
    error: 'Error',
    completed: 'Completed',
    travellerActionDisclamer:
      'Please note:\nEditing the action here only updates it for this traveller only, to change it for all future travellers update it on the journey and re-publish.',
    dateSent: 'Date sent',
    actionNotConfigured: 'Action not configured',
    noAssignee: 'No assignee',
    assignTo: 'Assign to...',
    hour: 'Hour',
    minute: 'Minute',
    nrDay: '1 day',
    nrDays: '{nr} days',
    nrHour: '1 hour',
    nrHours: '{nr} hours',
    nrMinute: '1 minute',
    nrMinutes: '{nr} minutes',
    journeyDate: 'Journey date',
    dateValue: 'Date value from journey',
    fixedDate: 'Fixed date',
    selectDate: 'Select date',
    manuallyUploded: 'Manually uploaded',
    traveller: 'Traveller',
    file: 'File',
    editAction: 'Edit',
    skipAction: 'Skip',
    unskipAction: 'Unskip',
    confirmUnskip: 'Confirm unskip',
    confirmText: 'Please note that unskipping this action will cause it to start automatically',
    skipWarningText:
      'Skipping this action will break connected values between the actions below.\nAre you sure you want to skip this action?',
    warning: 'Warning',
    confirmActionLabel: 'Connected actions',
    value: 'Value',
    attributeNotFound: 'Attribute not found',
    userNotFound: 'User not found',
    optionNotFound: 'Option not found',
    messageLength: 'Message length {count} characters',
    warningText:
      'Messages longer than {gsm7maxlength} characters (or {ucs2maxlength} characters if special characters are used) are treated as two or more SMS messages by the service provider, try to keep them as short and concise as possible to reduce your SMS cost',
    messageDynamicVaribles:
      'This message uses dynamic variables so the message length cannot be determined for each sending',
    fieldNotFound:
      'Field can not be found and will therefore not be updated when the action is run',
    restartFromHere: 'Restart • From here',
    restartModalTitle: 'Restart from action',
    restartDescription:
      'Are you sure you want to restart the journey from this point?\n• All following actions on this branch will be reset\n• This action will be updated to have a manual trigger so you can start it whenever it suits you ',
    restart: 'Restart',
    actionsTobeReset: 'Action/s to be reset',
    cannotBeUndone: 'This can not be undone.',
    overrideCondition: 'Continue anyway',
    overrideConditionTooltip: 'Bypass the condition and continue the journey',
    overrideConditionTitle: 'Continue anyway',
    overrideConditionDescription:
      'This will bypass the condition check and continue the journey as if the condition was met.\n\nAll actions below this condition will be un-halted.',
    details: 'Details',
    activity: 'Activity',
  },
};
